import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Search, Eye, Upload, Download, Tag, X } from 'lucide-react';

export default function TenantCustomers() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', tags: [] });
  const [selected, setSelected] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [editTagsFor, setEditTagsFor] = useState(null);
  const [editTags, setEditTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState('');

  const fetchCustomers = async () => {
    const res = await api.get('/tenant/customers', { params: { search } });
    setCustomers(res.data.customers); setTotal(res.data.total);
  };
  useEffect(() => { fetchCustomers(); }, [search]);

  const handleSave = async () => {
    await api.post('/tenant/customers', form);
    setShowDialog(false); setForm({ name: '', email: '', phone: '', address: '', tags: [] }); setTagInput(''); fetchCustomers();
  };

  const viewDetail = async (id) => {
    const res = await api.get(`/tenant/customers/${id}`);
    setSelected(res.data);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput('');
  };

  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter(x => x !== t) });

  const handleExport = async () => {
    try {
      const res = await api.get('/tenant/customers/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `customers_${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { alert('Gagal export'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post('/tenant/customers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(`Import: ${res.data.imported} baru, ${res.data.skipped} dilewati${res.data.errors?.length ? '\n' + res.data.errors.join('\n') : ''}`);
      fetchCustomers();
    } catch { alert('Gagal import'); }
    e.target.value = '';
  };

  const openEditTags = (customer) => {
    setEditTagsFor(customer);
    setEditTags(customer.tags || []);
    setEditTagInput('');
  };

  const saveEditTags = async () => {
    await api.put(`/tenant/customers/${editTagsFor.id}/tags`, editTags);
    setEditTagsFor(null);
    fetchCustomers();
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  return (
    <div data-testid="tenant-customers-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Pelanggan ({total})</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}><Download size={14} className="mr-1" /> Export CSV</Button>
          <label>
            <Button variant="outline" size="sm" asChild><span><Upload size={14} className="mr-1" /> Import CSV</span></Button>
            <input type="file" accept=".csv" className="hidden" onChange={handleImport} />
          </label>
          <Button onClick={() => setShowDialog(true)} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" size="sm" data-testid="add-customer-btn">
            <Plus size={14} className="mr-1" /> Tambah
          </Button>
        </div>
      </div>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="relative"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari pelanggan..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="customer-search-input" /></div>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm" data-testid="customers-table">
            <thead><tr className="border-b border-slate-100">
              <th className="text-left py-3 px-4 text-slate-500 font-medium">Nama</th>
              <th className="text-left py-3 px-4 text-slate-500 font-medium">Email</th>
              <th className="text-left py-3 px-4 text-slate-500 font-medium">Tags</th>
              <th className="text-left py-3 px-4 text-slate-500 font-medium">Orders</th>
              <th className="text-left py-3 px-4 text-slate-500 font-medium">Total Spent</th>
              <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
            </tr></thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-3 px-4 font-medium">{c.name}</td>
                  <td className="py-3 px-4 text-slate-600">{c.email}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1 flex-wrap">
                      {(c.tags || []).map(t => (
                        <Badge key={t} variant="outline" className="text-xs bg-blue-50 text-blue-700">{t}</Badge>
                      ))}
                      <button onClick={() => openEditTags(c)} className="text-xs text-slate-400 hover:text-blue-600"><Tag size={12} /></button>
                    </div>
                  </td>
                  <td className="py-3 px-4">{c.total_orders}</td>
                  <td className="py-3 px-4">Rp {fmt(c.total_spent)}</td>
                  <td className="py-3 px-4 text-right"><Button variant="ghost" size="icon" onClick={() => viewDetail(c.id)}><Eye size={14} /></Button></td>
                </tr>
              ))}
              {!customers.length && <tr><td colSpan={6} className="py-12 text-center text-slate-400">Belum ada pelanggan</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add Customer Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Pelanggan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" /></div>
            <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-1 flex-wrap mb-2">
                {form.tags.map(t => (
                  <Badge key={t} variant="outline" className="bg-blue-50 text-blue-700 gap-1">{t}<button onClick={() => removeTag(t)}><X size={10} /></button></Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Tambah tag..." onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
                <Button type="button" variant="outline" size="sm" onClick={addTag}>+</Button>
              </div>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-customer-btn">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Detail Pelanggan</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><span className="text-slate-500">Nama:</span> <span className="font-medium">{selected.name}</span></div>
              <div><span className="text-slate-500">Email:</span> {selected.email}</div>
              <div><span className="text-slate-500">Telepon:</span> {selected.phone || '-'}</div>
              <div>
                <span className="text-slate-500">Tags:</span>{' '}
                {(selected.tags || []).map(t => (
                  <Badge key={t} variant="outline" className="text-xs bg-blue-50 text-blue-700 mr-1">{t}</Badge>
                ))}
                {!(selected.tags || []).length && <span className="text-slate-400">-</span>}
              </div>
              <div><span className="text-slate-500">Total Orders:</span> {selected.total_orders}</div>
              <div><span className="text-slate-500">Total Spent:</span> Rp {fmt(selected.total_spent)}</div>
              {selected.recent_orders?.length > 0 && (
                <div>
                  <p className="font-medium text-slate-700 mt-3 mb-2">Pesanan Terbaru:</p>
                  {selected.recent_orders.map((o, i) => (
                    <div key={i} className="flex justify-between py-1 border-b border-slate-50 text-xs">
                      <span className="font-mono">{o.order_number}</span>
                      <span>Rp {fmt(o.total)}</span>
                      <span className={`px-2 py-0.5 rounded-full ${o.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Tags Dialog */}
      <Dialog open={!!editTagsFor} onOpenChange={() => setEditTagsFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Tags — {editTagsFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1 flex-wrap min-h-[32px]">
              {editTags.map(t => (
                <Badge key={t} variant="outline" className="bg-blue-50 text-blue-700 gap-1">{t}<button onClick={() => setEditTags(editTags.filter(x => x !== t))}><X size={10} /></button></Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={editTagInput} onChange={(e) => setEditTagInput(e.target.value)} placeholder="Tag baru..." onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); const v = editTagInput.trim(); if (v && !editTags.includes(v)) setEditTags([...editTags, v]); setEditTagInput(''); }
              }} />
              <Button variant="outline" size="sm" onClick={() => { const v = editTagInput.trim(); if (v && !editTags.includes(v)) setEditTags([...editTags, v]); setEditTagInput(''); }}>+</Button>
            </div>
            <Button onClick={saveEditTags} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]">Simpan Tags</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
